import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Popover, Button, Tooltip, Flex, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { ReactComponent as QuestionIcon } from "images/questionIcon.svg";
import { ReactComponent as CloseIcon } from "images/helperCloseIcon.svg";
import { onboardingData, OnboardingElement } from "../../constants/onboarding";
import { useLocation } from "react-router-dom";
import { useStores } from "stores";
import { observer } from "mobx-react-lite";
import { action } from "mobx";
import { COMPANY_ROLE_ENUM } from "interfaces";
import { routes } from "constants/routes";

import styles from "./onboarding.module.css";

const popoverContent = (element: OnboardingElement, handleCloseTooltip: (elementId: string) => void) => (
  <Flex className="popover-primary">
    <Flex vertical gap="small" className={styles.tooltipContent}>
      {element.tooltip_name && <Typography.Text strong>{element.tooltip_name}</Typography.Text>}
      <Typography.Text>{element.tooltip}</Typography.Text>
    </Flex>
    <Button
      type="text"
      size="small"
      icon={<CloseOutlined />}
      onClick={() => handleCloseTooltip(element.ui_element_id)}
      className={styles.closeButton}
    />
  </Flex>
);

export const OnboardingTooltip: React.FC = observer(() => {
  const location = useLocation();
  const { user, actionModals } = useStores();
  const currentPath = location.pathname.replace("/", "");

  const [visibleElements, setVisibleElements] = useState<OnboardingElement[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [closedTooltips, setClosedTooltips] = useState<Set<string>>(new Set());
  const [helperButtonStatus, setHelperButtonStatus] = useState<"closed" | "opened">("closed");
  const [helperButtonIsDisabled, setHelperButtonIsDisabled] = useState(false);
  const [isShowPopoverOnHelperButton, setIsShowPopoverOnHelperButton] = useState(false);
  const [isModalOpens, setIsModalOpens] = useState(false);

  const helperButtonOnboardingElement = onboardingData.find((e) => e.ui_element_id === "helpers-button");

  const companyRole = user.userInfo?.company_role;

  const companyIsCustomer = companyRole === COMPANY_ROLE_ENUM.CUSTOMER;
  const companyIsLP = companyRole === COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER;

  const shouldShowTooltip = useMemo(
    () => (companyIsCustomer || companyIsLP) && currentPath !== routes.changePassword,
    [companyIsCustomer, companyIsLP, currentPath]
  );

  useEffect(() => {
    setIsShowPopoverOnHelperButton(localStorage.getItem("onboarding-tooltip-helper-button-is-showed") !== "true");
  }, []);

  const hideHelperButtonPopover = useCallback(() => {
    setIsShowPopoverOnHelperButton(false);
    localStorage.setItem("onboarding-tooltip-helper-button-is-showed", "true");
  }, []);

  const handleCloseTooltip = useCallback(
    (elementId: string) => {
      setClosedTooltips((prev) => {
        const newClosedTooltips = new Set(prev).add(elementId);
        if (newClosedTooltips.size === visibleElements.length) {
          setHelperButtonStatus("closed");
          setIsVisible(false);
        }
        return newClosedTooltips;
      });

      if (elementId === "helpers-button") {
        hideHelperButtonPopover();
      }
    },
    [visibleElements.length, hideHelperButtonPopover]
  );

  const handleToggle = useCallback(() => {
    if (visibleElements.length === 0) return;

    if (!isVisible) {
      setClosedTooltips(new Set());
    } else {
      if (isShowPopoverOnHelperButton) {
        hideHelperButtonPopover();
      }
    }

    setIsVisible(!isVisible);
    setHelperButtonStatus(helperButtonStatus === "closed" ? "opened" : "closed");
  }, [visibleElements.length, isVisible, isShowPopoverOnHelperButton, helperButtonStatus, hideHelperButtonPopover]);

  const updateVisibleElements = useCallback(() => {
    action(() => {
      setTimeout(() => {
        let wrapper = actionModals.modalIds.length > 0 ? document.getElementById("modal-overlay") : document;

        setIsModalOpens(wrapper !== document);

        const filteredElements = onboardingData.filter((element: OnboardingElement) => {
          const roleMatch = element.role.includes("ALL") || element.role.includes(companyRole || "");

          const pageMatch = !element.pages || element.pages.includes(currentPath);

          const elementExists = wrapper?.querySelector(`[id="${element.ui_element_id}"]:not([hidden])`) !== null;

          const isNotHelperButton = element.ui_element_id !== "helpers-button";

          return roleMatch && pageMatch && elementExists && isNotHelperButton;
        });

        setVisibleElements(filteredElements);
        setHelperButtonIsDisabled(filteredElements.length === 0);
      }, 500);
      setIsVisible(false);
      setHelperButtonStatus("closed");
      setHelperButtonIsDisabled(true);
    })();
  }, [actionModals.modalIds.length, companyRole, currentPath]);

  useEffect(() => {
    if (shouldShowTooltip) {
      updateVisibleElements();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyRole, currentPath, actionModals.isAnyModalOpen, actionModals.modalIds.length]);

  // Экспортируем функцию для использования в других компонентах
  window.updateOnboardingElements = updateVisibleElements;

  // Если открыта модалка - отслеживаем ее скролл и применяем к подсказкам
  useEffect(() => {
    let modal: Element | null;
    let modalPopoverElements: HTMLElement[] = [];

    const setElementsPosition = () => {
      modalPopoverElements.forEach((element) => {
        element.style.transform = `translateY(${modal ? -1 * modal.scrollTop : 0}px)`;
      });
    };

    if (actionModals.modalIds.length > 0 && isVisible) {
      modal = document.getElementById("modal-overlay");
      modalPopoverElements = Array.from(document.querySelectorAll(".ant-popover") || []);
      setElementsPosition();

      modal?.addEventListener("scroll", setElementsPosition);
    }

    return () => {
      modal?.removeEventListener("scroll", setElementsPosition);
    };
  }, [actionModals.modalIds.length, isVisible]);

  const HelperButton = () => (
    <Tooltip
      title={
        helperButtonIsDisabled
          ? "На этой странице нет подсказок"
          : helperButtonStatus === "closed"
            ? "Показать подсказки"
            : "Скрыть все подсказки"
      }
      placement="left"
    >
      <Button
        type="default"
        shape="circle"
        icon={helperButtonStatus === "closed" ? <QuestionIcon /> : <CloseIcon />}
        className={`${styles.helperButton} ${isModalOpens ? styles.helperButtonOnModal : ""}`}
        onClick={handleToggle}
        disabled={helperButtonIsDisabled}
        id={isShowPopoverOnHelperButton ? "helpers-button" : undefined}
      />
    </Tooltip>
  );

  return (
    <>
      {shouldShowTooltip ? (
        isShowPopoverOnHelperButton && helperButtonOnboardingElement ? (
          <Popover
            key={helperButtonOnboardingElement.ui_element_id}
            content={() => popoverContent(helperButtonOnboardingElement, handleCloseTooltip)}
            placement={helperButtonOnboardingElement.placement || "bottom"}
            open={true}
            className="popover-primary"
            color={"var(--ant-color-primary)"}
            autoAdjustOverflow={false}
          >
            <HelperButton />
          </Popover>
        ) : (
          <HelperButton />
        )
      ) : null}
      {isVisible &&
        visibleElements.map((element) => {
          if (closedTooltips.has(element.ui_element_id)) return null;

          const targetElement = document.querySelector(`[id="${element.ui_element_id}"]:not([hidden])`);

          if (!targetElement) return null;
          const rect = targetElement.getBoundingClientRect();

          const position = {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height,
          };
          if (!position) return null;

          return (
            <Popover
              key={element.ui_element_id}
              content={() => popoverContent(element, handleCloseTooltip)}
              trigger="click"
              placement={element.placement || "bottom"}
              open={true}
              className="popover-primary"
              color={"var(--ant-color-primary)"}
              autoAdjustOverflow={false}
            >
              <div
                className={styles.tooltipElement}
                style={{
                  position: element.ui_element_id === "main-menu" ? "fixed" : "absolute",
                  top: position.top,
                  left: position.left,
                  width: `${position.width}px`,
                  height: `${position.height}px`,
                }}
              />
            </Popover>
          );
        })}
    </>
  );
});
